import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

// Import translations (avoid importing from public/ to keep HMR stable)
import enTranslations from './locales/en.json'
import esTranslations from './locales/es.json'
import heTranslations from './locales/he.json'

// Override console methods globally to suppress i18next missing key warnings
if (typeof window !== 'undefined' && !(console.warn as any).__i18nSuppressed) {
  const originalWarn = console.warn
  const originalLog = console.log
  const originalError = console.error
  
  const shouldSuppress = (message: string): boolean => {
    // Suppress all i18next debug messages
    // Matches patterns like:
    // - "i18next::translator: missingKey es translation propertyNavigation.transactions"
    // - "i18next: languageChanged es"
    // - "i18next: initialized"
    return message.includes('i18next::translator: missingKey') || 
           (message.includes('missingKey') && message.includes('translation')) ||
           message.includes('i18next: languageChanged') ||
           message.includes('i18next: initialized') ||
           message.startsWith('i18next:')
  }
  
  console.warn = (...args: any[]) => {
    const message = args.map(arg => 
      typeof arg === 'string' ? arg : String(arg)
    ).join(' ')
    
    if (shouldSuppress(message)) {
      return
    }
    
    originalWarn.apply(console, args)
  }
  
  console.log = (...args: any[]) => {
    const message = args.map(arg => 
      typeof arg === 'string' ? arg : String(arg)
    ).join(' ')
    
    if (shouldSuppress(message)) {
      return
    }
    
    originalLog.apply(console, args)
  }
  
  console.error = (...args: any[]) => {
    // Next dev overlay logs `intercept-console-error.ts:42 undefined` when someone calls
    // console.error(undefined) / console.error() (common with some libs). This is almost
    // never actionable and can spam the console, especially during map interactions.
    // We drop these calls while keeping real errors intact.
    if (args.length === 0 || args.every(arg => arg === undefined)) {
      return
    }

    const message = args.map(arg => 
      typeof arg === 'string' ? arg : String(arg)
    ).join(' ')
    
    if (shouldSuppress(message)) {
      return
    }
    
    originalError.apply(console, args)
  }
  
  ;(console.warn as any).__i18nSuppressed = true
}

const resources = {
  en: {
    translation: enTranslations
  },
  es: {
    translation: esTranslations
  },
  he: {
    translation: heTranslations
  }
}

// Get initial language from localStorage if available
const getInitialLanguage = () => {
  if (typeof window !== 'undefined') {
    try {
      const languageStorage = localStorage.getItem('language-storage');
      if (languageStorage) {
        const parsed = JSON.parse(languageStorage);
        return parsed.state?.currentLanguage || 'en';
      }
    } catch (error) {
      console.warn('Error parsing language storage:', error);
    }
  }
  return 'en';
};

// Custom logger to suppress i18next debug messages
const customLogger = {
  log: (...args: any[]) => {
    const message = args.join(' ')
    // Suppress all i18next debug messages
    if (message.includes('missingKey') || 
        message.includes('i18next::translator') ||
        message.includes('i18next: languageChanged') ||
        message.includes('i18next: initialized') ||
        message.startsWith('i18next:')) {
      return
    }
    console.log(...args)
  },
  warn: (...args: any[]) => {
    const message = args.join(' ')
    // Suppress all i18next debug messages
    if (message.includes('missingKey') || 
        message.includes('i18next::translator') ||
        message.includes('i18next: languageChanged') ||
        message.includes('i18next: initialized') ||
        message.startsWith('i18next:')) {
      return
    }
    console.warn(...args)
  },
  error: (...args: any[]) => {
    console.error(...args)
  },
}

i18n
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    lng: getInitialLanguage(), // Read from localStorage
    debug: false,
    saveMissing: false,
    missingKeyHandler: (_lng: string, _ns: string, key: string) => key, // Suppress missing key warnings
    logger: customLogger,
    
    interpolation: {
      escapeValue: false,
    },
  } as any)

export default i18n
